import React, { useEffect, useMemo, useState } from 'react';
import { Printer, AlertCircle, RotateCcw, CheckSquare, Square, PackageCheck, History, X } from 'lucide-react';
import { CompanyFilterSidebar } from '../components/CompanyFilterSidebar';
import { FilterDropdown } from '../components/ui/FilterDropdown';
import { SearchBar } from '../components/ui/SearchBar';
import { Button } from '../components/ui/Button';
import { useToast } from '../components/ui/Toast';
import { useOrders } from '../contexts/OrderContext';
import { useActivityLog } from '../contexts/ActivityLogContext';
import { useNotifications } from '../contexts/NotificationContext';
import { cleanProductName, displayProductName } from '../utils/formatters';
import { filterTodayOrders } from '../utils/dateFilters';
import { useDocumentTitle } from '../utils/useDocumentTitle';
import { getCompanies } from '../utils/companyManagement';
export function LabelControl() {
  useDocumentTitle('Etiquetas');
  const {
    orders,
    updateLabelStatus,
    updateOrderStatus,
    refreshOrders
  } = useOrders();
  const {
    addLog
  } = useActivityLog();
  const {
    addNotification
  } = useNotifications();
  const {
    showToast
  } = useToast();

  // --- ESTADOS ---
  const [selectedCompanies, setSelectedCompanies] = useState<Set<string>>(new Set());
  const [typeFilter, setTypeFilter] = useState('Todo');
  const [branchFilter, setBranchFilter] = useState('Todas');
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<'pending' | 'generated' | 'all'>('all');
  const [selectedOrderIds, setSelectedOrderIds] = useState<Set<string>>(new Set());
  const [showHistory, setShowHistory] = useState(false);
  const [showTotalLabelsModal, setShowTotalLabelsModal] = useState(false);
  const [showDeliveryConfirmModal, setShowDeliveryConfirmModal] = useState(false);
  const [orderToDeliver, setOrderToDeliver] = useState<any>(null);
  useEffect(() => {
    if (refreshOrders) {
      const interval = setInterval(() => {
        refreshOrders();
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [refreshOrders]);

  // --- LÓGICA DE DATOS ---
  const todayOrders = useMemo(() => filterTodayOrders(orders), [orders]);
  const pendingLabels = todayOrders.filter(o => o.labelStatus === 'Pendiente' && o.status !== 'Entregado' && o.status !== 'Cancelado').reduce((sum, o) => sum + (o.quantity || 1), 0);
  const generatedLabels = todayOrders.filter(o => o.labelStatus === 'Generado' && o.status !== 'Entregado' && o.status !== 'Cancelado').reduce((sum, o) => sum + (o.quantity || 1), 0);
  const totalActiveLabels = todayOrders.filter(o => o.status !== 'Cancelado' && o.status !== 'Entregado').reduce((sum, o) => sum + (o.quantity || 1), 0);
  const metrics = [{
    label: 'Total de etiquetas',
    value: totalActiveLabels.toString(),
    onClick: () => {
      setViewMode('all');
      setSelectedOrderIds(new Set());
    },
    clickable: true
  }, {
    label: 'Etiquetas Generadas',
    value: generatedLabels.toString(),
    onClick: () => {
      setViewMode('generated');
      setSelectedOrderIds(new Set());
    },
    clickable: true
  }, {
    label: 'Etiquetas Pendientes',
    value: pendingLabels.toString(),
    onClick: () => {
      setViewMode('pending');
      setSelectedOrderIds(new Set());
    },
    clickable: true
  }];
  const companyBranches = useMemo(() => {
    if (selectedCompanies.size === 0) return ['Todas'];
    const companies = getCompanies();
    const branches = new Set<string>();
    companies.filter(c => selectedCompanies.has(c.name)).forEach(c => {
      if (c.branches && c.branches.length > 0) {
        c.branches.forEach((b: any) => {
          if (b.name) branches.add(b.name);
        });
      }
    });
    todayOrders.filter(o => selectedCompanies.has(o.company)).forEach(o => {
      if (o.branch) branches.add(o.branch);
    });
    return ['Todas', ...Array.from(branches).sort()];
  }, [todayOrders, selectedCompanies]);
  const ordersToDisplay = useMemo(() => {
    const filtered = todayOrders.filter(o => {
      if (o.status === 'Entregado' || o.status === 'Cancelado') return false;
      if (selectedCompanies.size > 0 && !selectedCompanies.has(o.company)) return false;
      if (viewMode === 'generated') {
        if (o.labelStatus !== 'Generado') return false;
      } else if (viewMode === 'pending') {
        if (o.labelStatus !== 'Pendiente') return false;
      }
      if (branchFilter !== 'Todas' && o.branch !== branchFilter) return false;
      if (typeFilter !== 'Todo' && o.catalogType !== typeFilter) return false;
      if (searchTerm && !o.name.toLowerCase().includes(searchTerm.toLowerCase()) && !o.orderNo.includes(searchTerm) && !o.product.toLowerCase().includes(searchTerm.toLowerCase())) {
        return false;
      }
      return true;
    });
    return filtered.flatMap(order => {
      const quantity = order.quantity || 1;
      return Array(quantity).fill(null).map((_, index) => ({
        ...order,
        id: order.id + '-' + index,
        originalId: order.id,
        quantity: 1,
        originalQuantity: order.quantity
      }));
    });
  }, [todayOrders, selectedCompanies, viewMode, typeFilter, branchFilter, searchTerm]);
  const handleCompanyToggle = (company: string) => {
    setSelectedCompanies(prev => {
      const next = new Set(prev);
      if (next.has(company)) {
        next.delete(company);
      } else {
        next.add(company);
      }
      return next;
    });
    setSelectedOrderIds(new Set());
    setBranchFilter('Todas');
  };
  const toggleOrderSelection = (orderId: string) => {
    const newSelected = new Set(selectedOrderIds);
    if (newSelected.has(orderId)) {
      newSelected.delete(orderId);
    } else {
      newSelected.add(orderId);
    }
    setSelectedOrderIds(newSelected);
  };
  const toggleSelectAll = () => {
    if (selectedOrderIds.size === ordersToDisplay.length) {
      setSelectedOrderIds(new Set());
    } else {
      setSelectedOrderIds(new Set(ordersToDisplay.map(o => o.id)));
    }
  };

  // --- FUNCIÓN DE IMPRESIÓN PRO (IFRAME INVISIBLE) ---
  const printContent = (htmlContent: string) => {
    const iframe = document.createElement('iframe');

    // Ocultamos el iframe completamente
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    document.body.appendChild(iframe);
    const doc = iframe.contentWindow?.document;
    if (!doc) return;
    doc.open();
    doc.write(htmlContent);
    doc.close();
    iframe.onload = () => {
      setTimeout(() => {
        iframe.contentWindow?.focus();
        iframe.contentWindow?.print();
        setTimeout(() => {
          if (document.body.contains(iframe)) {
            document.body.removeChild(iframe);
          }
        }, 2000);
      }, 500);
    };
  };

  // --- IMPRESIÓN ETIQUETAS (AVERY 5161 - CORREGIDA Y REFINADA) ---
  const handlePrint = (ordersToPrint: any[], markAsGenerated: boolean = true) => {
    let finalOrdersToPrint = ordersToPrint;
    if (selectedOrderIds.size > 0) {
      finalOrdersToPrint = ordersToPrint.filter(o => selectedOrderIds.has(o.id));
    }
    if (finalOrdersToPrint.length === 0) {
      showToast('warning', 'Sin selección', 'No hay etiquetas para imprimir');
      return;
    }
    const printCompanies = selectedCompanies.size > 0 ? Array.from(selectedCompanies) : Array.from(new Set(finalOrdersToPrint.map(o => o.company)));
    const chunkSize = 20;
    const pages: any[][] = [];
    for (let i = 0; i < finalOrdersToPrint.length; i += chunkSize) {
      pages.push(finalOrdersToPrint.slice(i, i + chunkSize));
    }
    const pagesHtml = pages.map(pageOrders => {
      const labelsHtml = pageOrders.map(order => {
        const productText = displayProductName(order.product);
        // FORMATO REFINADO: Menos negrita, número espaciado
        return `<div class="label">
                <div class="line1">${order.company} — 809 713 8527</div>
                <div class="line2">${order.branch} — ENVÍO</div>
                <div class="line3">${order.name}</div>
                <div class="line4">${productText}</div>
              </div>`;
      }).join('');
      return `<div class="page">${labelsHtml}</div>`;
    }).join('');

    // CSS REFINADO: Fuentes más limpias y finas
    const cssStyles = `
      @page { size: letter; margin: 0; }
      * { box-sizing: border-box; margin: 0; padding: 0; }
      body { 
        font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif; 
        background: white; 
        -webkit-font-smoothing: antialiased;
      }
      .page {
        width: 8.5in;
        height: 11in;
        padding-top: 0.5in;
        padding-bottom: 0.5in;
        padding-left: 0.156in;
        padding-right: 0.156in;
        display: grid;
        grid-template-columns: 4in 4in;
        grid-template-rows: repeat(10, 1in);
        column-gap: 0.188in;
        row-gap: 0;
        page-break-after: always;
      }
      .page:last-child { page-break-after: auto; }
      .label {
        width: 100%;
        height: 100%;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        text-align: center;
        overflow: hidden;
        padding: 0.05in 0.15in;
      }
      /* Ajuste de pesos y tamaños para que no se vea "gruesa" */
      .line1 { font-size: 8pt; color: #333; margin-bottom: 3px; white-space: nowrap; font-weight: 400; }
      .line2 { font-size: 8pt; color: #000; margin-bottom: 3px; text-transform: uppercase; letter-spacing: 0.5px; font-weight: 500; }
      .line3 { font-size: 11pt; color: #000; margin-bottom: 3px; font-weight: 700; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 100%; }
      .line4 { font-size: 9pt; color: #333; line-height: 1.15; max-height: 2.3em; overflow: hidden; font-weight: 400; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; }
      @media print { body { -webkit-print-color-adjust: exact; } }
    `;
    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Etiquetas Avery 5161</title>
        <style>${cssStyles}</style>
      </head>
      <body>${pagesHtml}</body>
      </html>`;
    printContent(htmlContent);
    if (markAsGenerated) {
      const uniqueOriginalIds = Array.from(new Set(finalOrdersToPrint.map(o => o.originalId || o.id)));
      updateLabelStatus(uniqueOriginalIds, 'Generado');
      const companyNames = printCompanies.join(', ');
      addLog('Etiquetas Impresas', `${finalOrdersToPrint.length} etiquetas de ${companyNames}`, 'order');
      showToast('success', 'Etiquetas impresas', `${finalOrdersToPrint.length} etiquetas generadas`);
      setSelectedOrderIds(new Set());
    } else {
      const companyNames = printCompanies.join(', ');
      addLog('Etiquetas Reimprimidas', `${finalOrdersToPrint.length} etiquetas de ${companyNames}`, 'order');
      showToast('info', 'Etiquetas reimprimidas', `${finalOrdersToPrint.length} etiquetas reimpresas`);
    }
  };

  // --- IMPRESIÓN CONDUCE ---
  const handlePrintDeliveryNote = () => {
    let ordersToPrint = ordersToDisplay;
    if (selectedOrderIds.size > 0) {
      ordersToPrint = ordersToDisplay.filter(o => selectedOrderIds.has(o.id));
    }
    if (ordersToPrint.length === 0) {
      showToast('warning', 'Sin órdenes', 'No hay órdenes para imprimir');
      return;
    }
    const printCompanies = selectedCompanies.size > 0 ? Array.from(selectedCompanies) : Array.from(new Set(ordersToPrint.map(o => o.company)));
    const uniqueBranches = new Set(ordersToPrint.map(o => o.branch));
    const branchDisplay = uniqueBranches.size === 1 ? ordersToPrint[0].branch : branchFilter !== 'Todas' ? branchFilter : 'Múltiples Sucursales';
    const today = new Date().toLocaleDateString('es-DO', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
    const time = new Date().toLocaleTimeString('es-DO', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
    const totalQuantity = ordersToPrint.length;
    const tableRows = ordersToPrint.map((order, index) => {
      return `
          <tr>
            <td style="text-align: center; color: #555;">${index + 1}</td>
            <td style="font-family: monospace; font-weight: 600;">${order.orderNo}</td>
            <td style="font-weight: 600;">${order.name}</td>
            <td>${cleanProductName(order.product)}</td>
            <td><span class="badge">${order.branch || 'Principal'}</span></td>
            <td style="text-align: center; font-weight: bold;">1</td>
          </tr>`;
    }).join('');
    const deliveryCss = `
      @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
      @page { size: letter landscape; margin: 0.4in; }
      body { font-family: 'Inter', sans-serif; margin: 0; padding: 0; color: #1f2937; font-size: 12px; -webkit-print-color-adjust: exact; }
      .header-bar { height: 8px; background: linear-gradient(90deg, #1f2937 0%, #3b82f6 100%); width: 100%; margin-bottom: 25px; }
      .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 30px; border-bottom: 1px solid #e5e7eb; padding-bottom: 20px; }
      .brand h1 { font-size: 24px; font-weight: 800; margin: 0; color: #111827; text-transform: uppercase; letter-spacing: -0.5px; }
      .brand p { font-size: 10px; color: #6b7280; margin: 4px 0 0 0; text-transform: uppercase; letter-spacing: 2px; font-weight: 600; }
      .doc-info { text-align: right; }
      .doc-title { font-size: 18px; font-weight: 700; color: #111827; margin-bottom: 4px; }
      .doc-date { font-size: 11px; color: #4b5563; }
      .info-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 15px; margin-bottom: 25px; }
      .info-card { background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 6px; padding: 12px 15px; }
      .card-label { font-size: 9px; text-transform: uppercase; color: #6b7280; font-weight: 700; margin-bottom: 4px; }
      .card-value { font-size: 13px; font-weight: 600; color: #111827; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
      table { width: 100%; border-collapse: collapse; margin-bottom: 25px; font-size: 11px; }
      thead { background-color: #1f2937; color: white; }
      th { padding: 10px 12px; text-align: left; font-weight: 600; text-transform: uppercase; font-size: 10px; letter-spacing: 0.5px; }
      td { padding: 8px 12px; border-bottom: 1px solid #f3f4f6; vertical-align: middle; }
      tbody tr:nth-child(even) { background-color: #f9fafb; }
      .badge { background: #eff6ff; color: #1d4ed8; padding: 2px 8px; border-radius: 999px; font-size: 9px; font-weight: 600; text-transform: uppercase; border: 1px solid #dbeafe; }
      .totals { display: flex; justify-content: flex-end; margin-bottom: 40px; }
      .total-box { background: #1f2937; color: white; padding: 10px 30px; border-radius: 6px; text-align: right; }
      .total-label { font-size: 10px; opacity: 0.8; text-transform: uppercase; margin-bottom: 2px; }
      .total-value { font-size: 20px; font-weight: 700; }
      .signatures { display: flex; justify-content: space-between; margin-top: 50px; padding: 0 50px; }
      .sig-block { width: 35%; text-align: center; }
      .sig-line { border-top: 1px solid #9ca3af; margin-bottom: 8px; }
      .sig-text { font-size: 10px; color: #6b7280; text-transform: uppercase; font-weight: 600; }
    `;
    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Conduce de Entrega</title>
        <style>${deliveryCss}</style>
      </head>
      <body>
        <div class="header-bar"></div>
        <div class="header">
          <div class="brand"><h1>La Cochera</h1><p>Restaurant & Delivery</p></div>
          <div class="doc-info"><div class="doc-title">CONDUCE DE ENTREGA</div><div class="doc-date">Fecha: ${today}</div><div class="doc-date">Hora: ${time}</div></div>
        </div>
        <div class="info-grid">
          <div class="info-card"><div class="card-label">Empresa Destino</div><div class="card-value">${printCompanies.join(', ')}</div></div>
          <div class="info-card"><div class="card-label">Sucursal / Área</div><div class="card-value">${branchDisplay}</div></div>
          <div class="info-card"><div class="card-label">Responsable Envío</div><div class="card-value">Logística / Despacho</div></div>
        </div>
        <table>
          <thead><tr><th style="width: 40px; text-align: center;">#</th><th style="width: 100px;">Código</th><th>Cliente</th><th>Producto</th><th>Sucursal</th><th style="width: 60px; text-align: center;">Cant.</th></tr></thead>
          <tbody>${tableRows}</tbody>
        </table>
        <div class="totals"><div class="total-box"><div class="total-label">Total Unidades</div><div class="total-value">${totalQuantity}</div></div></div>
        <div class="signatures">
          <div class="sig-block"><div class="sig-line"></div><div class="sig-text">Entregado Por</div></div>
          <div class="sig-block"><div class="sig-line"></div><div class="sig-text">Recibido Por</div></div>
        </div>
      </body>
      </html>`;
    printContent(htmlContent);
    addLog('Conduce Impreso', `${ordersToPrint.length} órdenes de ${printCompanies.join(', ')}`, 'order');
    showToast('success', 'Conduce impreso', `Conduce generado para: ${branchDisplay}`);
  };
  const handlePrintPending = () => {
    const pendingOrders = ordersToDisplay.filter(o => o.labelStatus === 'Pendiente');
    handlePrint(pendingOrders, true);
  };
  const handleReprintGenerated = () => {
    const generatedOrders = ordersToDisplay.filter(o => o.labelStatus === 'Generado');
    handlePrint(generatedOrders, false);
  };
  const handleMarkAsDelivered = (order: any) => {
    setOrderToDeliver(order);
    setShowDeliveryConfirmModal(true);
  };
  const confirmDelivery = () => {
    if (orderToDeliver) {
      const idToUpdate = orderToDeliver.originalId || orderToDeliver.id;
      updateOrderStatus(idToUpdate, 'Entregado');
      addLog('Pedido Entregado', `Orden #${orderToDeliver.orderNo} - ${orderToDeliver.name}`, 'order');
      addNotification({
        type: 'order',
        title: 'Pedido Entregado',
        message: `Tu pedido #${orderToDeliver.orderNo} ha sido entregado`,
        orderId: idToUpdate
      });
      showToast('success', 'Pedido entregado', `Orden #${orderToDeliver.orderNo} marcada como entregada`);
      setShowDeliveryConfirmModal(false);
      setOrderToDeliver(null);
    }
  };
  return <div className="flex flex-col lg:flex-row h-[calc(100vh-64px)] lg:h-screen bg-gray-50 dark:bg-gray-950 overflow-hidden" data-id="element-3823">
      <div className="w-full lg:w-80 p-4 lg:p-6 lg:pr-0 overflow-y-auto border-b lg:border-b-0 lg:border-r border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900" data-id="element-3824">
        <CompanyFilterSidebar multiSelect selectedCompanies={selectedCompanies} onCompanyToggle={handleCompanyToggle} data-id="element-3825" />
      </div>

      <div className="flex-1 p-4 lg:p-6 overflow-y-auto" data-id="element-3826">
        <div className="bg-white dark:bg-gray-900 rounded-xl p-4 lg:p-6 shadow-sm border border-gray-200 dark:border-gray-800 mb-6" data-id="element-3827">
          <div className="flex flex-col lg:flex-row justify-between items-start gap-4 mb-6" data-id="element-3828">
            <div data-id="element-3829">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4" data-id="element-3830">
                Control de impresión de Etiquetas
              </h2>

              {!selectedCompanies.size && <div className="mb-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg flex items-start gap-2" data-id="element-3831">
                  <AlertCircle className="text-yellow-600 dark:text-yellow-400 shrink-0 mt-0.5" size={18} data-id="element-3832" />
                  <p className="text-sm text-yellow-800 dark:text-yellow-200" data-id="element-3833">
                    Selecciona una o más empresas del menú lateral para imprimir
                    etiquetas
                  </p>
                </div>}

              <div className="flex flex-col sm:flex-row flex-wrap gap-3" data-id="element-3834">
                <Button onClick={handlePrintPending} variant="primary" leftIcon={<Printer className="w-4 h-4" data-id="element-3836" />} disabled={ordersToDisplay.filter(o => o.labelStatus === 'Pendiente').length === 0} className="w-full sm:w-auto justify-center" data-id="element-3835">
                  {selectedOrderIds.size > 0 ? `Imprimir Seleccionadas (${selectedOrderIds.size})` : 'Imprimir Pendientes'}
                </Button>

                <Button onClick={handleReprintGenerated} variant="secondary" leftIcon={<RotateCcw className="w-4 h-4" data-id="element-3838" />} disabled={ordersToDisplay.filter(o => o.labelStatus === 'Generado').length === 0} className={`w-full sm:w-auto justify-center ${viewMode === 'pending' ? 'hidden' : ''}`} data-id="element-3837">
                  {selectedOrderIds.size > 0 ? `Reimprimir Seleccionadas (${selectedOrderIds.size})` : 'Reimprimir Generadas'}
                </Button>

                <Button onClick={handlePrintDeliveryNote} variant="outline" leftIcon={<PackageCheck className="w-4 h-4" data-id="element-3840" />} disabled={ordersToDisplay.length === 0} className="w-full sm:w-auto justify-center border-green-600 text-green-600 hover:bg-green-50 dark:border-green-500 dark:text-green-400 dark:hover:bg-green-900/20" data-id="element-3839">
                  {selectedOrderIds.size > 0 ? `Conduce (${selectedOrderIds.size})` : 'Imprimir Conduce'}
                </Button>

                <Button variant={showHistory ? 'primary' : 'secondary'} leftIcon={<History className="w-4 h-4" data-id="element-3842" />} onClick={() => {
                setShowHistory(!showHistory);
                setSelectedOrderIds(new Set());
              }} className="w-full sm:w-auto justify-center" data-id="element-3841">
                  {showHistory ? 'Ver Pendientes' : 'Ver Historial'}
                </Button>
              </div>
            </div>

            <div className="flex flex-wrap gap-4 w-full lg:w-auto" data-id="element-3843">
              {metrics.map((metric, index) => <div key={index} onClick={metric.clickable ? metric.onClick : undefined} className={'border rounded-xl p-4 flex-1 lg:flex-none lg:w-40 transition-all' + (metric.clickable ? ' cursor-pointer hover:shadow-md' : '') + (metric.label === 'Total de etiquetas' && viewMode === 'all' ? ' bg-blue-50 dark:bg-blue-900/20 border-blue-300 dark:border-blue-700 ring-2 ring-blue-200 dark:ring-blue-800' : metric.label === 'Etiquetas Generadas' && viewMode === 'generated' ? ' bg-green-50 dark:bg-green-900/20 border-green-300 dark:border-green-700 ring-2 ring-green-200 dark:ring-green-800' : metric.label === 'Etiquetas Pendientes' && viewMode === 'pending' ? ' bg-yellow-50 dark:bg-yellow-900/20 border-yellow-300 dark:border-yellow-700 ring-2 ring-yellow-200 dark:ring-yellow-800' : ' bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700')} data-id="element-3844">
                  <span className="text-xs text-gray-500 dark:text-gray-400 font-medium block mb-1" data-id="element-3845">
                    {metric.label}
                  </span>
                  <p className={'text-2xl font-bold' + (metric.label === 'Total de etiquetas' && viewMode === 'all' ? ' text-blue-700 dark:text-blue-400' : metric.label === 'Etiquetas Generadas' && viewMode === 'generated' ? ' text-green-700 dark:text-green-400' : metric.label === 'Etiquetas Pendientes' && viewMode === 'pending' ? ' text-yellow-700 dark:text-yellow-400' : ' text-gray-900 dark:text-white')} data-id="element-3846">
                    {metric.value}
                  </p>
                </div>)}
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-900 rounded-xl p-4 lg:p-6 shadow-sm border border-gray-200 dark:border-gray-800" data-id="element-3847">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6" data-id="element-3848">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white" data-id="element-3849">
              {viewMode === 'all' ? 'Todas las Etiquetas' : viewMode === 'generated' ? 'Etiquetas Generadas' : 'Etiquetas Pendientes'}
              {selectedCompanies.size > 0 && ' - ' + Array.from(selectedCompanies).join(', ')}
              {selectedOrderIds.size > 0 && <span className="ml-2 text-sm font-normal text-blue-600 dark:text-blue-400" data-id="element-3850">
                  ({selectedOrderIds.size} seleccionadas)
                </span>}
            </h3>

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full sm:w-auto" data-id="element-3851">
              <FilterDropdown label="Sucursal" value={branchFilter} options={companyBranches} onChange={setBranchFilter} className="w-full sm:w-48" data-id="element-3852" />
              <FilterDropdown label="Tipo" value={typeFilter} options={['Todo', 'Almuerzo', 'Cena']} onChange={setTypeFilter} className="w-full sm:w-40" data-id="element-3853" />
              <SearchBar className="w-full sm:w-64" placeholder="Buscar por nombre, orden..." onSearch={setSearchTerm} data-id="element-3854" />
            </div>
          </div>

          {ordersToDisplay.length === 0 ? <div className="text-center py-12 text-gray-500 dark:text-gray-400" data-id="element-3855">
              <Printer size={48} className="mx-auto text-gray-300 dark:text-gray-600 mb-4" data-id="element-3856" />
              <p data-id="element-3857">
                {viewMode === 'all' ? 'No hay etiquetas activas' : viewMode === 'generated' ? 'No hay etiquetas generadas' : 'No hay etiquetas pendientes'}
              </p>
              <p className="text-sm mt-2" data-id="element-3858">
                {selectedCompanies.size > 0 ? 'Intenta con otros filtros' : 'Las etiquetas aparecerán cuando haya pedidos del día'}
              </p>
            </div> : <div data-id="element-3859">
              <div className="hidden md:block overflow-x-auto" data-id="element-3860">
                <table className="w-full text-left border-collapse" data-id="element-3861">
                  <thead className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700" data-id="element-3862">
                    <tr data-id="element-3863">
                      <th className="px-4 py-3 w-10" data-id="element-3864">
                        <button onClick={toggleSelectAll} className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200" data-id="element-3865">
                          {selectedOrderIds.size > 0 && selectedOrderIds.size === ordersToDisplay.length ? <CheckSquare size={18} className="text-blue-600" data-id="element-3866" /> : <Square size={18} data-id="element-3867" />}
                        </button>
                      </th>
                      <th className="px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-3868">
                        No. Orden
                      </th>
                      <th className="px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-3869">
                        Cliente
                      </th>
                      <th className="px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-3870">
                        Empresa
                      </th>
                      <th className="px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-3871">
                        Sucursal
                      </th>
                      <th className="px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-3872">
                        Producto
                      </th>
                      <th className="px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-3873">
                        Tipo
                      </th>
                      <th className="px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-3874">
                        Estado
                      </th>
                      <th className="px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase text-right" data-id="element-3875">
                        Acciones
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700" data-id="element-3876">
                    {ordersToDisplay.map(order => <tr key={order.id} className={'hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer' + (selectedOrderIds.has(order.id) ? ' bg-blue-50 dark:bg-blue-900/10' : '')} onClick={() => toggleOrderSelection(order.id)} data-id="element-3877">
                        <td className="px-4 py-3" onClick={e => e.stopPropagation()} data-id="element-3878">
                          <button onClick={() => toggleOrderSelection(order.id)} className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200" data-id="element-3879">
                            {selectedOrderIds.has(order.id) ? <CheckSquare size={18} className="text-blue-600" data-id="element-3880" /> : <Square size={18} data-id="element-3881" />}
                          </button>
                        </td>
                        <td className="px-4 py-3 text-sm font-mono text-gray-900 dark:text-white" data-id="element-3882">
                          {order.orderNo}
                        </td>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white" data-id="element-3883">
                          {order.name}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300" data-id="element-3884">
                          {order.company}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300" data-id="element-3885">
                          {order.branch}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300" data-id="element-3886">
                          {displayProductName(order.product)}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300" data-id="element-3887">
                          {order.catalogType}
                        </td>
                        <td className="px-4 py-3" data-id="element-3888">
                          <span className={order.labelStatus === 'Generado' ? 'px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400' : 'px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400'} data-id="element-3889">
                            {order.labelStatus}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right" onClick={e => e.stopPropagation()} data-id="element-3890">
                          {order.status === 'Listo' && <button onClick={() => handleMarkAsDelivered(order)} className="text-green-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-300 text-sm font-medium flex items-center gap-1 ml-auto" data-id="element-3891">
                              <PackageCheck size={14} data-id="element-3892" />
                              Entregar
                            </button>}
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>

              {/* Mobile Card View */}
              <div className="md:hidden space-y-3" data-id="element-3893">
                <div className="flex items-center justify-between px-1 mb-2" data-id="element-3894">
                  <button onClick={toggleSelectAll} className="flex items-center gap-2 text-sm font-medium text-gray-600 dark:text-gray-300" data-id="element-3895">
                    {selectedOrderIds.size > 0 && selectedOrderIds.size === ordersToDisplay.length ? <CheckSquare size={18} className="text-blue-600" data-id="element-3896" /> : <Square size={18} data-id="element-3897" />}
                    Seleccionar todos
                  </button>
                  <span className="text-xs text-gray-400" data-id="element-3898">
                    {ordersToDisplay.length} órdenes
                  </span>
                </div>

                {ordersToDisplay.map(order => <div key={order.id} className={`bg-white dark:bg-gray-900 p-4 rounded-xl border shadow-sm transition-colors ${selectedOrderIds.has(order.id) ? 'border-blue-300 dark:border-blue-700 bg-blue-50/50 dark:bg-blue-900/10' : 'border-gray-200 dark:border-gray-800'}`} onClick={() => toggleOrderSelection(order.id)} data-id="element-3899">
                    <div className="flex justify-between items-start mb-3" data-id="element-3900">
                      <div className="flex items-center gap-3" data-id="element-3901">
                        <button onClick={e => {
                    e.stopPropagation();
                    toggleOrderSelection(order.id);
                  }} className="text-gray-500 dark:text-gray-400" data-id="element-3902">
                          {selectedOrderIds.has(order.id) ? <CheckSquare size={20} className="text-blue-600" data-id="element-3903" /> : <Square size={20} data-id="element-3904" />}
                        </button>
                        <div data-id="element-3905">
                          <div className="flex items-center gap-2" data-id="element-3906">
                            <span className="text-xs font-mono font-bold text-gray-500 dark:text-gray-400" data-id="element-3907">
                              #{order.orderNo}
                            </span>
                            <span className="text-xs text-gray-400" data-id="element-3908">
                              • {order.catalogType}
                            </span>
                          </div>
                          <h3 className="font-bold text-gray-900 dark:text-white" data-id="element-3909">
                            {order.name}
                          </h3>
                        </div>
                      </div>
                      <span className={order.labelStatus === 'Generado' ? 'px-2 py-1 text-[10px] font-bold rounded-full bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400' : 'px-2 py-1 text-[10px] font-bold rounded-full bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400'} data-id="element-3910">
                        {order.labelStatus}
                      </span>
                    </div>

                    <div className="pl-8 space-y-2 mb-3" data-id="element-3911">
                      <div className="text-sm text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-800/50 p-2 rounded-lg" data-id="element-3912">
                        {displayProductName(order.product)}
                      </div>
                      <div className="flex flex-col text-xs text-gray-500 dark:text-gray-400" data-id="element-3913">
                        <span className="font-medium text-gray-900 dark:text-white" data-id="element-3914">
                          {order.company}
                        </span>
                        <span data-id="element-3915">{order.branch}</span>
                      </div>
                    </div>

                    {order.status === 'Listo' && <div className="pl-8 pt-2 border-t border-gray-100 dark:border-gray-800" data-id="element-3916">
                        <button onClick={e => {
                  e.stopPropagation();
                  handleMarkAsDelivered(order);
                }} className="w-full flex items-center justify-center gap-2 py-2 text-xs font-bold text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20 rounded-lg" data-id="element-3917">
                          <PackageCheck size={16} data-id="element-3918" /> Marcar Entregado
                        </button>
                      </div>}
                  </div>)}
              </div>
            </div>}
        </div>
      </div>

      {showDeliveryConfirmModal && orderToDeliver && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-id="element-3919">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl w-full max-w-md border border-gray-200 dark:border-gray-800" data-id="element-3920">
            <div className="p-6 border-b border-gray-200 dark:border-gray-800" data-id="element-3921">
              <div className="flex items-center gap-3" data-id="element-3922">
                <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg" data-id="element-3923">
                  <PackageCheck className="w-6 h-6 text-green-600 dark:text-green-400" data-id="element-3924" />
                </div>
                <div data-id="element-3925">
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white" data-id="element-3926">
                    Confirmar Entrega
                  </h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-3927">
                    {'Orden #' + orderToDeliver.orderNo}
                  </p>
                </div>
              </div>
            </div>
            <div className="p-6" data-id="element-3928">
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 mb-4" data-id="element-3929">
                <p className="text-sm font-medium text-yellow-900 dark:text-yellow-100 mb-2" data-id="element-3930">
                  ⚠️ Confirmación de Entrega
                </p>
                <p className="text-xs text-yellow-800 dark:text-yellow-200" data-id="element-3931">
                  Esta acción marcará el pedido como entregado y NO se puede
                  revertir.
                </p>
              </div>
              <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg" data-id="element-3932">
                <p className="text-sm text-gray-600 dark:text-gray-300" data-id="element-3933">
                  <strong data-id="element-3934">Cliente:</strong> {orderToDeliver.name}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-300" data-id="element-3935">
                  <strong data-id="element-3936">Producto:</strong>{' '}
                  {cleanProductName(orderToDeliver.product)}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-300" data-id="element-3937">
                  <strong data-id="element-3938">Empresa:</strong> {orderToDeliver.company}
                </p>
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 dark:border-gray-800 flex gap-3" data-id="element-3939">
              <Button variant="outline" onClick={() => {
            setShowDeliveryConfirmModal(false);
            setOrderToDeliver(null);
          }} fullWidth data-id="element-3940">
                Cancelar
              </Button>
              <Button onClick={confirmDelivery} fullWidth className="bg-green-600 hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-800" data-id="element-3941">
                Confirmar Entrega
              </Button>
            </div>
          </div>
        </div>}

      {showTotalLabelsModal && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-id="element-3942">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl w-full max-w-4xl border border-gray-200 dark:border-gray-800 max-h-[90vh] overflow-hidden flex flex-col" data-id="element-3943">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-800" data-id="element-3944">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white" data-id="element-3945">
                Total de Etiquetas Activas
              </h2>
              <button onClick={() => setShowTotalLabelsModal(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" data-id="element-3946">
                <X size={20} data-id="element-3947" />
              </button>
            </div>
            <div className="p-0 overflow-y-auto flex-1" data-id="element-3948">
              <div className="p-6 text-center text-gray-500" data-id="element-3949">
                Tabla de resumen de etiquetas activas
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 dark:border-gray-800" data-id="element-3950">
              <Button onClick={() => setShowTotalLabelsModal(false)} fullWidth data-id="element-3951">
                Cerrar
              </Button>
            </div>
          </div>
        </div>}
    </div>;
}